import { type FC, useMemo, lazy, Suspense, type ComponentProps } from "react";
import { colors } from "@root/shared/styles/sprinkles.css";
import { retryLazyLoad } from "@root/shared/utils/retryLazyLoad";
import type { Bar as BarType } from "react-chartjs-2";
import { type ReportGraphProps } from "../../types/type";
import * as styles from "./styles.css";

type HighlightType = "gold" | "silver" | "bronze" | "potential" | "average";

type HighlightItem = {
  type: HighlightType;
  index: number;
};

export type Props = {
  title: string;
  labels: string[];
  values: number[];
  highlights: readonly HighlightItem[];
};

const HIGHLIGHT_COLORS = {
  gold: "#EBCC66",
  silver: "#AEBBCF",
  bronze: "#E3A272",
  potential: "#FF8E80",
  normal: "#CCCCCC",
} as const;

const BarComponent = lazy(() =>
  retryLazyLoad<ComponentProps<typeof BarType>>({
    fn: () => import("react-chartjs-2").then(({ Bar }) => ({ default: Bar })),
  }),
);

export const ReportGraph: FC<ReportGraphProps> = ({ title, labels, values, highlights }) => {
  const maxDataValue = Math.max(...values);
  const minValue = 0;
  const stepSize = maxDataValue / 6.5;
  const yAxisMax = minValue + stepSize * 7;

  const data = useMemo(() => {
    const getBarColor = (index: number): string => {
      const matchedHighlight = highlights.find((highlight) => highlight.index === index);
      return matchedHighlight ? HIGHLIGHT_COLORS[matchedHighlight.type] : colors.BorderLight;
    };

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: values.map((_, i) => getBarColor(i)),
        },
      ],
    };
  }, [labels, values, highlights]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: title,
        fontSize: 14,
        fontColor: "#FF817D",
        padding: 16,
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              drawOnChartArea: false,
              tickMarkLength: 8,
              offsetGridLines: false,
              color: "#666",
            },
            ticks: {
              autoSkip: false,
              minRotation: 45,
              padding: 7,
              fontSize: 10,
              fontColor: colors.TextMedium,
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              drawBorder: false,
              tickMarkLength: 0,
              zeroLineColor: colors.GrayScale94,
            },
            ticks: {
              display: false,
              stepSize,
              min: minValue,
              max: yAxisMax,
            },
          },
        ],
      },
      hover: {
        mode: null,
      },
      tooltips: {
        enabled: false,
      },
    }),
    [title, stepSize, yAxisMax, minValue],
  );

  return (
    <div className={styles.wrapper}>
      <Suspense fallback={<div>Loading...</div>}>
        <BarComponent data={data} options={options} />
      </Suspense>
    </div>
  );
};
